import { fillText } from 'components/invoiceImport/invoiceImportText';
import {
  INVOICE_ERROR_TEXTS,
  UNKNOWN_ERROR_TEXTS,
  formatVatRate,
} from 'components/invoiceImportJournal/invoiceJournalText';
import { formatParisDay } from 'components/invoiceList/invoiceStatusTooltipText';
import { normalizePennylaneCategoryId } from 'helper/companyOverviewDiff';
import { paymentTermLabel } from 'constants/paymentTerms';

/**
 * What the detail of one invoice says of its error (JBWEB-1274), read in the
 * locales under `invoiceErrors.<code>` with this table as the default. The
 * journal words the same fourteen codes for a group of invoices; here it is
 * one invoice, so only its label is shared (INVOICE_ERROR_TEXTS). The fixes
 * follow the api README, § 4.
 */
export const INVOICE_DETAIL_ERROR_TEXTS = {
  ERR_BESTT_FACTURE_INTROUVABLE: {
    explanation: 'Bestt dit ne pas connaître cette facture.',
    fix: 'Vérifiez cette facture dans Bestt, puis réessayez.',
  },
  ERR_BESTT_API: {
    explanation: 'Bestt a échoué sur cette facture.',
    fix: 'Attendez quelques minutes, puis réessayez.',
  },
  ERR_ENTREPRISE_INCONNUE_JOBBIZ: {
    explanation:
      'Aucune entreprise Jobbiz n’a le client Bestt de cette facture.',
    fix: 'Renseignez l’identifiant Bestt dans la fiche de l’entreprise, puis réessayez.',
  },
  ERR_ENTREPRISE_AMBIGUE_JOBBIZ: {
    explanation:
      'Plusieurs entreprises Jobbiz ont le client Bestt de cette facture.',
    fix: 'Ne laissez cet identifiant Bestt qu’à une seule entreprise, puis réessayez.',
  },
  ERR_BESTT_PDF_INDISPONIBLE: {
    explanation: 'Bestt n’a pas rendu le PDF de cette facture.',
    fix: 'Attendez quelques minutes, puis réessayez.',
  },
  ERR_STOCKAGE_PDF: {
    explanation: 'Le PDF de cette facture n’a pas pu être rangé ou relu.',
    fix: 'Attendez quelques minutes, puis réessayez.',
  },
  ERR_PENNYLANE_DESACTIVE: {
    explanation: 'Hors production, rien n’est envoyé à Pennylane.',
    fix: 'Rien à corriger : normal hors production.',
  },
  ERR_ENTREPRISE_INTROUVABLE_PENNYLANE: {
    explanation: 'Aucun client Pennylane ne correspond à l’entreprise.',
    fix: 'Créez le client dans Pennylane avec le SIRET de l’entreprise, puis réessayez.',
  },
  ERR_ENTREPRISE_AMBIGUE_PENNYLANE: {
    explanation:
      'Plusieurs clients Pennylane ont le SIRET ou le SIREN de l’entreprise.',
    fix: 'N’en gardez qu’un dans Pennylane, puis réessayez.',
  },
  ERR_FACTURE_AMBIGUE_PENNYLANE: {
    explanation:
      'Pennylane tient le numéro de cette facture plusieurs fois, ou seulement en version archivée : Jobbiz ne peut pas savoir laquelle suivre.',
    fix: 'Dans Pennylane, n’en gardez qu’une, non archivée, puis réessayez.',
  },
  // The invoice's rate fills the explanation.
  ERR_TVA_TAUX_INCONNU: {
    explanation:
      'Le taux de {rate} appliqué à cette facture n’est pas dans la table de Jobbiz.',
    explanationNoRate:
      'Le taux de TVA de cette facture n’est pas dans la table de Jobbiz.',
    fix: 'Prévenez l’équipe technique pour qu’elle ajoute ce taux, puis réessayez.',
  },
  ERR_PENNYLANE_API: {
    explanation: 'Pennylane a refusé cette facture ou n’a pas répondu.',
    fix: 'Attendez quelques minutes, puis réessayez.',
  },
  ERR_PENNYLANE_CATEGORIE: {
    explanation:
      'Cette facture est dans Pennylane ; réessayer ne pose que la catégorie.',
    fix: 'Réessayez : seule la catégorie sera posée.',
  },
  ERR_PENNYLANE_ECHEANCE: {
    explanation:
      'Cette facture était déjà dans Pennylane ; l’échéance choisie pour elle n’y a pas été posée.',
    fix: 'Réessayez : seule l’échéance sera envoyée.',
  },
  ERR_PENNYLANE_ANNEXE: {
    explanation:
      'Cette facture est importée, mais une de ses annexes n’a pas pu être envoyée dans Pennylane.',
    fix: 'Si une annexe est « à vérifier dans Pennylane », cherchez-la d’abord dans Pennylane : supprimez-la dans Jobbiz, et ajoutez-la à nouveau si Pennylane ne l’a pas. Réessayez ensuite : seules les annexes en attente seront envoyées.',
  },
};

const isOwnKey = (table, key) =>
  Object.prototype.hasOwnProperty.call(table, key);

const isAmount = (value) => typeof value === 'number' && Number.isFinite(value);

/**
 * The label, explanation and fix of an invoice's error. `errorText` reads the
 * `invoiceErrors` space, `journalText` the `invoiceJournal` one (labels, and
 * the texts of a code the screen does not know, shown as is).
 */
export const invoiceErrorTexts = (errorText, journalText, invoice) => {
  const code = invoice.error?.code;
  if (!isOwnKey(INVOICE_DETAIL_ERROR_TEXTS, code)) {
    return {
      label: code ?? '',
      explanation: journalText(
        'unknownError.explanation',
        UNKNOWN_ERROR_TEXTS.explanation,
      ),
      fix: journalText('unknownError.fix', UNKNOWN_ERROR_TEXTS.fix),
    };
  }
  const defaults = INVOICE_DETAIL_ERROR_TEXTS[code];
  const read = (key) => errorText(`${code}.${key}`, defaults[key]);
  let explanation = read('explanation');
  if (code === 'ERR_TVA_TAUX_INCONNU') {
    explanation = isAmount(invoice.vatRate)
      ? fillText(explanation, { rate: formatVatRate(invoice.vatRate) })
      : read('explanationNoRate');
  }
  return {
    label: journalText(`errors.${code}.label`, INVOICE_ERROR_TEXTS[code].label),
    explanation,
    fix: read('fix'),
  };
};

/** "Client Bestt 47" without a company name, "—" without a client either. */
export const companyLabelOf = (text, invoice) => {
  if (invoice.company?.name) return invoice.company.name;
  if (invoice.besttClientId != null) {
    return fillText(text('besttClient', 'Client Bestt {id}'), {
      id: invoice.besttClientId,
    });
  }
  return '—';
};

/** "2026/06" for "2026-06", as the mockups write the period. */
export const formatPeriod = (period) =>
  period ? String(period).replace('-', '/') : '—';

/**
 * The fields of the detail where the status badge reads the list's: one
 * badge, and one tooltip, for the list and the detail.
 */
export const toStatusBadgeInvoice = (invoice) => ({
  ...invoice,
  errorCode: invoice.error?.code ?? null,
  remainingAmount: invoice.payment?.remainingAmount ?? null,
  paidAmount: invoice.payment?.paidAmount ?? null,
  paidAt: invoice.payment?.paidAt ?? null,
});

const isDiscovered = (invoice) => invoice.importStatus === 'discovered';

/**
 * The category line. A discovered invoice has none of its own yet: it shows
 * the company's, which the import will set (api README § 4, rule 6), unless
 * an exception was set on it. `categories` is null while they are read.
 */
export const categoryText = (text, invoice, categories) => {
  const own = invoice.category ?? {};
  const usesCompany =
    isDiscovered(invoice) && own.source !== 'invoice' && own.id == null;
  const id = usesCompany ? invoice.companyDefaults?.categoryId : own.id;
  if (usesCompany && id == null && !invoice.company) return '—';
  let name;
  if (id == null) name = text('information.noCategory', 'Aucune catégorie');
  else if (categories === null) name = '…';
  else {
    const found = categories.find(
      (category) =>
        normalizePennylaneCategoryId(category.id) ===
        normalizePennylaneCategoryId(id),
    );
    name =
      found?.label ??
      fillText(text('information.unknownCategory', 'Catégorie n° {id}'), {
        id,
      });
  }
  if (usesCompany) {
    return fillText(text('information.atImport', '{value} · à l’import'), {
      value: name,
    });
  }
  return own.source === 'invoice'
    ? fillText(text('information.categoryModified', '{value} · modifiée'), {
        value: name,
      })
    : name;
};

/**
 * The payment term line: the term, where it comes from, and the due date. A
 * discovered invoice shows the company's term, which the import will apply.
 */
export const paymentTermText = (text, invoice, language) => {
  const own = invoice.paymentTerm;
  if (!own) {
    const companyTerm = invoice.company
      ? invoice.companyDefaults?.paymentTerm
      : null;
    const label =
      isDiscovered(invoice) && companyTerm
        ? paymentTermLabel(companyTerm, language)
        : null;
    if (!label) return '—';
    const value = invoice.companyDefaults.paymentTermIsDefault
      ? fillText(text('information.termDefault', '{value} · par défaut'), {
          value: label,
        })
      : label;
    return fillText(text('information.atImport', '{value} · à l’import'), {
      value,
    });
  }
  let value =
    paymentTermLabel(own, language) ??
    fillText(text('information.termDays', '{days} jours'), {
      days: own.days,
    });
  if (own.source === 'invoice') {
    value = fillText(text('information.termModified', '{value} · modifié'), {
      value,
    });
  } else if (own.source === 'default') {
    value = fillText(text('information.termDefault', '{value} · par défaut'), {
      value,
    });
  }
  const dueDate = formatParisDay(invoice.dueDate);
  return dueDate
    ? fillText(
        text('information.withDueDate', '{value} · échéance le {date}'),
        {
          value,
          date: dueDate,
        },
      )
    : value;
};
